import type { CountryInfoWithCurrency } from '@shared/types/country-info';
import clsx from 'clsx';
import { FC, useMemo } from 'react';
import { useTranslation as useTranslationHook } from 'react-i18next';
import { icons } from '../../constants/assets';

type CurrencyType = 'POWER' | 'FIAT';

interface PowerFiatSwitcherProps {
  onCurrencyChange: (currency: CurrencyType) => void;
  value: CurrencyType;
  countryInfo: CountryInfoWithCurrency;
  className?: string;
}

export const PowerFiatSwitcher: FC<PowerFiatSwitcherProps> = ({
  onCurrencyChange,
  value,
  countryInfo,
  className,
}) => {
  const isFiat = useMemo(() => value === 'FIAT', [value]);
  const { t } = useTranslationHook();

  return (
    <div className={clsx('bg-power-fiat-switcher flex w-[288px] shrink-0 flex-col items-start justify-center gap-4 rounded-lg px-4 py-3 text-left', className)}>
      <div className="flex flex-col items-start justify-center gap-1">
        <h2 className="font-objektiv-mk3 text-[15px] font-bold normal-case leading-tight text-white">
          {t('powerFiatSwitcher.title')}
        </h2>
        <p className="font-objektiv-mk3 text-[10px] font-bold normal-case leading-relaxed text-[#BDAFD2]">
          {t('powerFiatSwitcher.description')}
        </p>
      </div>
      <div className="flex w-full items-center justify-between rounded-lg bg-[#0E122C]/50 p-3">
        <div className="flex flex-1 items-center gap-1">
          <img
            src={icons.powerIcon}
            alt="POWER coin"
            className="mb-0.5 size-5"
          />
          <span
            className={clsx(
              'text-[14.5px] font-black uppercase',
              !isFiat ? 'text-neon_yellow' : 'text-white/50',
            )}
          >
            {t('powerFiatSwitcher.power')}
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            onCurrencyChange(value === 'POWER' ? 'FIAT' : 'POWER');
          }}
          className="relative mx-2 inline-flex h-5 w-8 items-center rounded-full p-0.5 transition-colors focus:outline-none"
          style={{
            border: '2px solid rgba(255, 255, 255, 0.40)',
          }}
          role="switch"
          aria-checked={isFiat}
          aria-label="Toggle currency"
        >
          <span className="absolute inset-0.5 rounded-full bg-gray-400 transition-colors" />
          <span
            className={clsx(
              'relative inline-block size-3 rounded-full bg-white transition-transform duration-200 ease-in-out',
              isFiat ? 'translate-x-3' : 'translate-x-0',
            )}
          />
        </button>
        <div className="flex flex-1 items-center justify-end gap-1">
          <span
            className={clsx(
              'text-nowrap text-[14.5px] font-black uppercase',
              isFiat ? 'text-neon_yellow' : 'text-white/50',
            )}
          >
            {`${countryInfo.currency}-${countryInfo.currencySymbol}`}
          </span>
          <span
            className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-sm"
            role="img"
            aria-label={`${countryInfo.name} flag`}
          >
            <img
              src={countryInfo.flagSvg}
              alt=""
              className="rounded-sm object-cover"
            />
          </span>
        </div>
      </div>
    </div>
  );
};

export default PowerFiatSwitcher;
